import { SIDE_EVENTS_HOST } from "@shared/sideEventsHub";

const SUBDOMAIN_SECTION_MAP: Record<string, string> = {
  ewit: "network",
};

const SECTION_SUBDOMAIN_MAP: Record<string, string> = {
  network: "ewit",
};

function isDevHostname(hostname: string): boolean {
  if (hostname === "localhost" || hostname === "127.0.0.1") return true;
  if (/^\d+\.\d+\.\d+\.\d+$/.test(hostname)) return true;
  if (hostname.includes("replit")) return true;
  if (hostname.endsWith(".repl.co")) return true;
  return false;
}

/** Detect the standalone capital domain (chiefintech.capital) */
export function isCapitalDomain(): boolean {
  if (typeof window === "undefined") return false;
  const hostname = window.location.hostname.toLowerCase();
  return hostname === "chiefintech.capital" || hostname === "www.chiefintech.capital";
}

/** Detect the standalone Chief in AI domain (chiefinai.com) */
export function isChiefInAIDomain(): boolean {
  if (typeof window === "undefined") return false;
  const hostname = window.location.hostname.toLowerCase();
  return hostname === "chiefinai.com" || hostname === "www.chiefinai.com";
}

const CHIEFINAI_RESERVED_SUBDOMAINS = new Set(["www", "api", "mail", "smtp", "imap"]);
const COMPANION_PREFIX = "/companion";

/**
 * Detect a companion event subdomain (e.g. cannes.chiefinai.com → "cannes").
 * Returns the slug, or null when not on a companion subdomain.
 * Bare chiefinai.com, www, and reserved subdomains return null.
 */
export function getCompanionSubdomain(): string | null {
  if (typeof window === "undefined") return null;
  return getCompanionSubdomainForHostname(window.location.hostname);
}

/** Resolve companion routing from a hostname without requiring browser globals. */
export function getCompanionSubdomainForHostname(hostname: string): string | null {
  hostname = hostname.trim().toLowerCase();
  if (hostname === SIDE_EVENTS_HOST) return "events";
  if (isDevHostname(hostname)) return null;
  if (!hostname.endsWith(".chiefinai.com")) return null;
  const sub = hostname.slice(0, -".chiefinai.com".length);
  if (!sub || sub.includes(".")) return null;
  if (CHIEFINAI_RESERVED_SUBDOMAINS.has(sub)) return null;
  return sub;
}

/** Internal base path that a companion subdomain maps to. */
function companionBasePath(sub: string): string {
  return sub === "events"
    ? `${COMPANION_PREFIX}/events-overview`
    : `${COMPANION_PREFIX}/${sub}`;
}

/**
 * On a companion subdomain, map the clean browser path to the internal
 * /companion/:subdomain/... path so the SPA routes match.
 * e.g. on cannes.chiefinai.com: "/" → "/companion/cannes", "/map" → "/companion/cannes/map".
 * On any other host, the path is returned unchanged.
 */
export function normalizeCompanionPath(locationPath: string): string {
  if (typeof window === "undefined") return locationPath;
  return normalizeCompanionPathForHostname(window.location.hostname, locationPath);
}

/** Pure hostname-aware variant used by routing tests and the browser wrapper. */
export function normalizeCompanionPathForHostname(hostname: string, locationPath: string): string {
  const sub = getCompanionSubdomainForHostname(hostname);
  if (!sub) return locationPath;
  const base = companionBasePath(sub);
  if (locationPath === base || locationPath.startsWith(base + "/")) return locationPath;
  if (locationPath === "/" || locationPath === "") return base;
  return base + locationPath;
}

/**
 * On a companion subdomain, strip the internal prefix so navigation keeps
 * clean URLs in the address bar.
 * e.g. "/companion/cannes/map" → "/map", "/companion/cannes" → "/".
 * On any other host, the href is returned unchanged.
 */
export function companionHref(href: string): string {
  const sub = getCompanionSubdomain();
  if (!sub) return href;
  const base = companionBasePath(sub);
  if (href === base) return "/";
  if (href.startsWith(base + "/")) return href.slice(base.length);
  return href;
}

function getBaseDomain(): string {
  const hostname = window.location.hostname;
  if (isDevHostname(hostname)) return hostname;
  if (isCapitalDomain()) return "chiefintech.com";
  if (isChiefInAIDomain()) return "chiefintech.com";
  const parts = hostname.split(".");
  if (parts.length >= 3) {
    const first = parts[0].toLowerCase();
    if (first === "www" || SUBDOMAIN_SECTION_MAP[first]) {
      return parts.slice(1).join(".");
    }
    return parts.slice(1).join(".");
  }
  return hostname;
}

export function getActiveSubdomain(): string | null {
  const hostname = window.location.hostname;
  if (isDevHostname(hostname)) return null;
  if (isCapitalDomain()) return null; // capital is a separate domain, not a subdomain
  if (isChiefInAIDomain()) return null; // chiefinai is a separate domain, not a subdomain
  const parts = hostname.split(".");
  if (parts.length < 3) return null;
  const sub = parts[0].toLowerCase();
  if (sub === "www") return null;
  return SUBDOMAIN_SECTION_MAP[sub] ? sub : null;
}

export function getSubdomainSection(): string | null {
  if (isCapitalDomain()) return "capital";
  if (isChiefInAIDomain()) return "chiefinai";
  const sub = getActiveSubdomain();
  if (!sub) return null;
  return SUBDOMAIN_SECTION_MAP[sub] || null;
}

export function isSubdomainActive(): boolean {
  return getActiveSubdomain() !== null || isCapitalDomain() || isChiefInAIDomain();
}

export function getMainDomainUrl(path: string = "/"): string {
  const hostname = window.location.hostname;
  if (isDevHostname(hostname)) return path;
  const baseDomain = getBaseDomain();
  return `${window.location.protocol}//www.${baseDomain}${path}`;
}

export function getSubdomainUrl(section: string, path: string = "/"): string {
  const subdomain = SECTION_SUBDOMAIN_MAP[section];
  if (!subdomain) return path;

  const hostname = window.location.hostname;
  if (isDevHostname(hostname)) return path;
  const baseDomain = getBaseDomain();
  return `${window.location.protocol}//${subdomain}.${baseDomain}${path}`;
}

export function needsCrossDomainNav(targetSection: string): boolean {
  if (isDevHostname(window.location.hostname)) return false;

  // On the capital domain, everything except "capital" needs to cross-link
  if (isCapitalDomain()) {
    return targetSection !== "capital";
  }
  // On the chiefinai domain, everything except "chiefinai" needs to cross-link
  if (isChiefInAIDomain()) {
    return targetSection !== "chiefinai";
  }

  const currentSubdomain = getActiveSubdomain();
  const currentSection = currentSubdomain ? SUBDOMAIN_SECTION_MAP[currentSubdomain] : null;

  if (targetSection === "network" && currentSection !== "network") return true;
  if (targetSection !== "network" && currentSection === "network") return true;
  return false;
}

export function getCrossDomainHref(targetSection: string, href: string): string {
  if (targetSection === "network") {
    return getSubdomainUrl("network", href);
  }
  return getMainDomainUrl(href);
}

export function resolveNavHref(href: string, currentSubdomain: string | null, targetSection?: string): string {
  // On the capital domain, non-fund links go to main domain
  if (isCapitalDomain() && targetSection && targetSection !== "capital") {
    return getMainDomainUrl(href);
  }
  // On the chiefinai domain, non-AI links go to main domain
  if (isChiefInAIDomain() && targetSection && targetSection !== "chiefinai") {
    return getMainDomainUrl(href);
  }

  if (!currentSubdomain) return href;

  const subdomainSection = SUBDOMAIN_SECTION_MAP[currentSubdomain];
  if (targetSection && targetSection !== subdomainSection) {
    return getMainDomainUrl(href);
  }

  return href;
}
